import { Camera } from 'lucide-react';
import { useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import useScreenshotCapture, {
    type ScreenshotUnavailableReason,
} from '~/hooks/viewer/useScreenshotCapture';

/** The words each unavailable reason shows in the camera's tooltip. */
const UNAVAILABLE_REASON_MESSAGE_IDS: Record<
    ScreenshotUnavailableReason,
    string
> = {
    noActiveTile: 'tool.disabled.noActiveTile',
    paneShowsNoImage: 'tool.disabled.paneShowsNoImage',
};

/**
 * Standalone "Take screenshot" toolbar tool. Saves the engine-composited frame —
 * the background image, contour outlines, and the difference overlay when active
 * — as a PNG the user can share with the manufacturer to report findings, e.g. a
 * disagreement with an AI-generated structure. Engine custom objects (brush cursor,
 * transform gizmo) are part of that frame and do appear; measurements, crosshairs
 * and POI markers are DOM layers above it and are not captured.
 *
 * This component is UI only: the button, and the confirmation dialog that guards
 * against exporting burned-in patient identifiers. All capture and PHI-gating
 * policy lives in {@link useScreenshotCapture} so it can be unit-tested
 * against the real burned-in gate rather than a mock.
 */
export default function ScreenshotTool() {
    const intl = useIntl();
    const {
        canCapture,
        unavailableReason,
        isConfirmOpen,
        requestCapture,
        confirmCapture,
        cancelCapture,
    } = useScreenshotCapture();
    // A disabled camera names its cause, so the user knows what to do next.
    const tooltip = buildToolTooltip(
        intl,
        intl.formatMessage({ id: 'toolbar.screenshot.tooltip' }),
        unavailableReason
            ? intl.formatMessage({
                  id: UNAVAILABLE_REASON_MESSAGE_IDS[unavailableReason],
              })
            : undefined
    );

    return (
        <>
            <ToolbarButton
                icon={<Camera />}
                tooltip={tooltip}
                disabled={!canCapture}
                onClick={requestCapture}
                data-testid="tool-viewport-screenshot"
            />
            <ConfirmationDialog
                open={isConfirmOpen}
                mode="warning"
                title={intl.formatMessage({
                    id: 'screenshot.burnedIn.confirmTitle',
                })}
                message={intl.formatMessage({
                    id: 'screenshot.burnedIn.confirmBody',
                })}
                confirmText={intl.formatMessage({
                    id: 'screenshot.burnedIn.confirmAction',
                })}
                onConfirm={confirmCapture}
                onCancel={cancelCapture}
            />
        </>
    );
}
